import React from 'react';

// Components
import { FormattedMessageV2 } from '@/views/components/i18n';
import { PrimaryButton, SecondaryButton } from '@/views/components/buttons';
import { Tooltip } from '@/views/components/tooltips';

// Hooks
import { useSelector } from 'react-redux';

// Redux
import { hasNextPortfolio } from '@/redux/modules/ai-statement-reader-multi-portfolio';

// Types
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatMessage } from '@/utils/i18n';

type Props = {
  isSaveDisabled: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => Promise<void>;
};

const MultiPortfoliosEditorFooterActions = ({ isSaveDisabled, isSaving, onDiscard, onSave }: Props) => {
  const { currentPortfolioIndex, portfolios } = useSelector((state: ReduxState) => state.aiStatementReaderMultiPortfolio);
  const hasMorePortfolios = useSelector((state: ReduxState) => hasNextPortfolio(state.aiStatementReaderMultiPortfolio));

  const currentIndex = currentPortfolioIndex ?? 0;
  const i18nBasePath = 'PortfolioEditorBuilder.Footer';
  const totalPortfolios = portfolios.length;

  const getSaveButtonTextKey = (): string => {
    if (isSaving) {
      return `${i18nBasePath}.Saving`;
    }
    return `${i18nBasePath}.MultiPortfolios.SaveAnd${hasMorePortfolios ? 'Continue' : 'Finish'}`;
  };

  return (
    <div className="portfolio-editor-footer-actions multi-portfolios">
      <FormattedMessageV2
        className="counter-label"
        toLocalize={`${i18nBasePath}.MultiPortfolios.ReviewProgress`}
        values={{ current: currentIndex + 1, total: totalPortfolios }}
      />

      <SecondaryButton
        className="secondary-v2 portfolio-editor-footer-discard"
        onClick={onDiscard}
        disabled={isSaving}
        data-testid="portfolioEditorFooter-secondaryButton-discard"
      >
        <FormattedMessageV2 toLocalize={`${i18nBasePath}.MultiPortfolios.DiscardPortfolio`} />
      </SecondaryButton>

      <Tooltip className="custom-tooltip-v2 save-button-container" disabled={!isSaving} message={formatMessage(`${i18nBasePath}.SavingChanges`)}>
        <div className="tooltip-wrapper">
          <PrimaryButton
            className="portfolio-editor-footer-save-portfolio primary-v2"
            disabled={isSaveDisabled}
            loading={isSaving}
            onClick={onSave}
            data-testid="portfolioEditorFooter-primaryButton-savePortfolio"
          >
            <FormattedMessageV2 toLocalize={getSaveButtonTextKey()} />
          </PrimaryButton>
        </div>
      </Tooltip>
    </div>
  );
};

export default MultiPortfoliosEditorFooterActions;
